import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

export interface Client {
  id: number;
  name: string;
  email: string | null;
  status: string;
  source: string;
  last_contact: string;
}

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching clients...');
      
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, email, status, source, last_contact')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching clients:', error);
        toast({
          variant: "destructive",
          title: "Error fetching clients",
          description: error.message
        });
        throw error;
      }

      console.log('Fetched clients:', data);
      setClients(data || []);
    } catch (error: any) {
      console.error('Error in fetchClients:', error);
      toast({
        variant: "destructive",
        title: "Failed to load clients",
        description: "Please try refreshing the page"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClient = async (clientId: number) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Client deleted successfully"
      });

      fetchClients();
    } catch (error: any) {
      console.error('Error deleting client:', error);
      toast({
        variant: "destructive",
        title: "Error deleting client",
        description: error.message
      });
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return { clients, isLoading, handleDeleteClient };
};